import api from "$utils/api";
import {
  type Author,
  type Document as APIDocument,
  type DocumentPermissions,
  type Folder as APIFolder,
  type FolderList,
  type FolderPermissions,
  type DocumentWithPermissions,
  CategoryTypeEnum,
} from "@outverse-video/outverse-api-client-lib";
import { deserialiseCRDT, serialiseCRDT } from "app/components/editor/utils";
import { Privacy } from "app/models/community";
import { DocumentTypeEnum, type Document } from "app/models/documents";
import { sveltekitApiUrl } from "$utils/urls";
import { PREVENT_POLLING } from "$utils/consts";
import { alerts } from "./alert";
import { CanceledError } from "axios";

const FOLDERS_REFRESH_MS = PREVENT_POLLING ? 100000000 : 10000; // 10s

export interface JSONDocument
  extends Omit<APIDocument, "authors" | "folder" | "content" | "title"> {
  myPermissions: DocumentPermissions;
  authors: Array<Author>;
  folder: FolderList;
  title: string; // override title to be required
  content: {
    crdt: Uint8Array;
    json: { [key: string]: any };
    text: string;
    title: string; // override title to be required
  };
  docType?: DocumentTypeEnum;
}

export interface JSONFolder extends APIFolder {
  parentUuid?: string;
  myPermissions: FolderPermissions;
  folderItemType: "nested_folder";
}

export interface FolderTransportLayer {
  fetch: (communitySlug: string) => Promise<JSONFolder[]>;

  createFolder: (
    communitySlug: string,
    categoryType: CategoryTypeEnum,
    name?: string,
    icon?: string,
    iconDark?: string
  ) => Promise<JSONFolder>;
  updateFolder: (folder: APIFolder, communitySlug: string) => Promise<JSONFolder>;
  deleteFolder: (uuid: string, communitySlug: string) => Promise<void>;
  moveFolder(communitySlug: string, uuid: string, index: number): unknown;

  createDocument: (
    communitySlug: string,
    isSnippet?: boolean,
    isPublished?: boolean,
    snippetData?: { title: string; content: { text: string; json: any; crdt: string } }
  ) => Promise<JSONDocument>;
  fetchDocument: (request: {
    fetch: typeof fetch;
    communitySlug: string;
    uuid: string;
  }) => Promise<JSONDocument | null>;
  saveDraftDocument: (
    doc: Pick<Document, "content" | "uuid">,
    communitySlug: string,
    jsonDraft: { [p: string]: any }
  ) => Promise<JSONDocument>;
  updateDocument: (doc: Partial<Document>, communitySlug: string) => Promise<JSONDocument>;
  deleteDocument: (doc: string, communitySlug: string) => Promise<void>;
  moveDocument(communitySlug: string, uuid: string, uuid1: string, index: number): unknown;

  onReceiveUpdate: (communitySlug: string, updater: (response: JSONFolder[]) => void) => () => void;
  fetchSnippet: (request: {
    fetch: typeof fetch;
    communitySlug: string;
    uuid: string;
  }) => Promise<JSONDocument | null>;
}

export const documentAPI: FolderTransportLayer = {
  fetch: async (communitySlug: string) => {
    return (await api.communitiesFoldersList({ communitySlug })).data.map((f) => ({
      ...f.folder,
      myPermissions: f.myPermissions,
      folderItemType: "nested_folder",
    }));
  },
  // Would need fixing in the API, create doesn't return myPermissions
  // @ts-ignore
  createFolder: async (
    communitySlug: string,
    categoryType: CategoryTypeEnum,
    name?: string,
    icon?: string,
    iconDark?: string
  ) => {
    return (
      await api.communitiesFoldersCreate({
        communitySlug,
        folder: {
          title: name || "New folder",
          categoryType,
          icon: icon || null,
          iconDark: iconDark || null,
        } as APIFolder,
      })
    ).data;
  },
  // Would need fixing in the API, update doesn't return myPermissions
  // @ts-ignore
  updateFolder: async (folder: APIFolder, communitySlug: string) => {
    return (
      await api.communitiesFoldersPartialUpdate({
        uuid: folder.uuid,
        communitySlug,
        patchedFolder: folder,
      })
    ).data;
  },
  deleteFolder: async (uuid: string, communitySlug: string) => {
    try {
      return (await api.communitiesFoldersDestroy({ uuid, communitySlug })).data;
    } catch (error) {
      if (error instanceof CanceledError) {
        alerts.error("Failed to delete folder.");
      } else {
        throw error;
      }
    }
  },
  /** Fetches a document from the API using the provided fetch function, and deserialises the CRDT.
   *
   * Requires the fetch function to enable use of sveltes custom fetch function for SSR.
   */
  fetchDocument: async (request: { fetch: typeof fetch; communitySlug: string; uuid: string }) => {
    const resp = await request.fetch(
      `${sveltekitApiUrl()}/community/${request.communitySlug}/doc/${request.uuid}`
    );
    // need to override types for authors/folder which are objects not strings here
    if (!resp.ok) {
      return null;
    }
    const space = (await resp.json()) as DocumentWithPermissions;
    const doc = {
      ...space.document,
      myPermissions: space.myPermissions,
    } as any as JSONDocument;
    doc.content.crdt = deserialiseCRDT(space.document.content.crdt);
    return doc;
  },
  createDocument: async (
    communitySlug: string,
    isSnippet: boolean = false,
    isPublished: boolean = false,
    snippetData?: { title: string; content: { text: string; json: any; crdt: string } }
  ) => {
    const resp = await api.communitiesDocumentsCreate({
      communitySlug,
      document: {
        privacy: Privacy.Public,
        permissions: [],
        status: isPublished ? "published" : "draft",
        authors: [],
        content: snippetData?.content || {
          crdt: "",
          text: "",
          json: {},
        },
        title: snippetData?.title || (isSnippet ? "Untitled snippet" : "Untitled document"),
        isSnippet,
      } as any as APIDocument,
    });
    const doc = {
      ...resp.data,
    } as any as JSONDocument;

    doc.content.crdt = deserialiseCRDT(resp.data.content.crdt);
    return doc;
  },
  saveDraftDocument: async (
    doc: Pick<Document, "content" | "uuid">,
    communitySlug: string,
    jsonDraft: { [p: string]: any }
  ) => {
    const patchedDoc: Partial<APIDocument> = { ...doc } as any;
    if (doc.content) {
      // serialise doc
      patchedDoc.content = {
        ...doc.content,
        crdt: serialiseCRDT(doc.content.crdt),
      };
    }
    const resp = await api.communitiesDocumentsDraft({
      uuid: doc.uuid!,
      communitySlug,
      cRDTDraft: {
        ...patchedDoc.content!,
        json: jsonDraft,
      },
    });

    const updatedDoc = {
      ...resp.data,
    } as any as JSONDocument;
    updatedDoc.content.crdt = deserialiseCRDT(resp.data.content.crdt);
    return updatedDoc;
  },
  updateDocument: async (doc: Partial<Document>, communitySlug: string) => {
    const patchedDoc: Partial<APIDocument> = { ...doc } as any;

    if (doc.content) {
      // serialise doc
      patchedDoc.content = {
        ...doc.content,
        crdt: serialiseCRDT(doc.content.crdt),
      };
    }
    if ("folder" in doc) {
      patchedDoc.folder = doc.folder?.uuid;
    }
    if ("authors" in doc && doc.authors) {
      patchedDoc.authors = doc.authors.map((author) => author.uuid);
    }

    const resp = await api.communitiesDocumentsPartialUpdate({
      uuid: doc.uuid!,
      communitySlug,
      patchedDocument: patchedDoc,
    });

    const updatedDoc = {
      ...resp.data,
    } as any as JSONDocument;
    updatedDoc.content.crdt = deserialiseCRDT(resp.data.content.crdt);
    return updatedDoc;
  },
  deleteDocument: async (doc: string, communitySlug: string) => {
    try {
      return (await api.communitiesDocumentsDestroy({ uuid: doc, communitySlug })).data;
    } catch (error) {
      if (error instanceof CanceledError) {
        alerts.error("Failed to delete document.");
      } else {
        throw error;
      }
    }
  },
  moveFolder: async (communitySlug: string, uuid: string, index: number) => {
    try {
      return await api.communitiesMoveSpaceGroup({
        slug: communitySlug,
        ordering: {
          uuid,
          order: index + 1,
        },
      });
    } catch (error) {
      if (error instanceof CanceledError) {
        alerts.error("Failed to move folder.");
      } else {
        throw error;
      }
    }
  },
  moveDocument: async (communitySlug: string, uuid: string, folder: string, index: number) => {
    try {
      return await api.communitiesFoldersMoveDocument({
        communitySlug: communitySlug,
        uuid: folder,
        ordering: {
          uuid,
          order: index,
        },
      });
    } catch (error) {
      if (error instanceof CanceledError) {
        alerts.error("Failed to move document.");
      } else {
        throw error;
      }
    }
  },
  onReceiveUpdate: function (communitySlug: string, updater: (response: JSONFolder[]) => void) {
    const interval = setInterval(
      async () => updater(await this.fetch(communitySlug)),
      FOLDERS_REFRESH_MS
    );
    return () => clearInterval(interval);
  },

  fetchSnippet: async (request: { fetch: typeof fetch; communitySlug: string; uuid: string }) => {
    const resp = await request.fetch(
      `${sveltekitApiUrl()}/community/${request.communitySlug}/doc/${request.uuid}`
    );

    if (!resp.ok) {
      return null;
    }

    const space = (await resp.json()) as DocumentWithPermissions;
    const doc = {
      ...space.document,
      myPermissions: space.myPermissions,
    } as any as JSONDocument;

    doc.content.crdt = deserialiseCRDT(space.document.content.crdt);
    return doc;
  },
};
